import { browserLocalStorage, type UserScopedStorage } from '@/lib/storage/user-scoped';

/**
 * A sign-out the server has not confirmed yet (#119 E2E D1).
 *
 * Sign-out marks this before it posts `/auth/logout`, and lifts it only when
 * the server answers (ended the session, or there was none: 401). "Sign out
 * anyway" while offline therefore leaves the mark behind, together with a
 * refresh cookie the server still honours. Until a later `/auth/logout`
 * succeeds, nothing may use that cookie: the boot posts the logout before any
 * refresh, `refreshSession` refuses, and the sign-in paths settle it first.
 * Otherwise the next person at a shared computer is signed in as the last one.
 *
 * Kept apart from the API client so the client can read it without a cycle.
 * Outside the user-data prefix, so the sign-out sweep keeps it. Every access is
 * wrapped; a browser that refuses storage cannot carry a mark between loads.
 */
export const LOGOUT_PENDING_KEY = 'auth-logout-pending';

export function markLogoutPending(storage: UserScopedStorage | null = browserLocalStorage()): void {
  try {
    storage?.setItem(LOGOUT_PENDING_KEY, String(Date.now()));
  } catch {
    // Storage refused: the in-page sign-out still runs.
  }
}

export function isLogoutPending(storage: UserScopedStorage | null = browserLocalStorage()): boolean {
  try {
    return storage?.getItem(LOGOUT_PENDING_KEY) != null;
  } catch {
    return false;
  }
}

/**
 * How long an SSO round trip may take for its return to lift the mark (#119
 * I3). The API keeps a login state for OIDC_AUTH_STATE_TTL_SECONDS (600 s by
 * default) from `/auth/oidc/start`; the mark is set just before the browser
 * goes there, so the window is that TTL plus a margin for the redirects. A
 * deployment that raises the TTL beyond 15 minutes must raise this too.
 */
export const SSO_SUPERSEDE_MAX_AGE_MS = 15 * 60 * 1000;
const SSO_PREFIX = 'sso:';

/** A fresh nonce for one SSO round trip: URL-safe, as the API's `client_nonce` requires. */
export function newSsoNonce(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * An explicit SSO sign-in goes ahead although the old session could not be
 * ended (coordinator decision: a failing logout never locks the browser out).
 * The mark stays (an abandoned round trip leaves the old cookie alive) and
 * records the round trip's nonce. The API hands that nonce back in the app
 * redirect's fragment only once the login has completed and replaced the
 * cookie, so only that landing may lift the mark (`ssoReturnCompleted`).
 */
export function markSsoSupersedes(nonce: string, storage: UserScopedStorage | null = browserLocalStorage(), now: number = Date.now()): void {
  try {
    storage?.setItem(LOGOUT_PENDING_KEY, `${SSO_PREFIX}${now}:${nonce}`);
  } catch {
    // Storage refused: no mark to carry either.
  }
}

/**
 * What this page load is to the SSO sign-in the mark records:
 * - `completed`: the fragment carries its nonce (sent back by a completed login
 *   only), within SSO_SUPERSEDE_MAX_AGE_MS of its start: the mark may go;
 * - `late`: the fragment carries its nonce, but outside that window (or the
 *   start time is unreadable). The cookie is the new login's, so it must not
 *   be revoked by settling the mark now; it is not restored either (I3);
 * - `none`: no nonce of this round trip (opened by hand, a refused or
 *   abandoned login, no SSO mark): the mark stays and is settled as usual.
 */
export type SsoReturn = 'completed' | 'late' | 'none';

export function ssoReturnState(hash: string, storage: UserScopedStorage | null = browserLocalStorage(), now: number = Date.now()): SsoReturn {
  try {
    const mark = storage?.getItem(LOGOUT_PENDING_KEY);
    if (!mark?.startsWith(SSO_PREFIX)) return 'none';
    const [rawAt, nonce] = mark.slice(SSO_PREFIX.length).split(':');
    if (!nonce || new URLSearchParams(hash.replace(/^#/, '')).get('sso') !== nonce) return 'none';
    const at = Number(rawAt);
    if (!Number.isFinite(at) || now - at < 0 || now - at > SSO_SUPERSEDE_MAX_AGE_MS) return 'late';
    return 'completed';
  } catch {
    return 'none';
  }
}

/** This page load is the in-time return of the SSO sign-in the mark records (`ssoReturnState`). */
export function ssoReturnCompleted(hash: string, storage: UserScopedStorage | null = browserLocalStorage(), now: number = Date.now()): boolean {
  return ssoReturnState(hash, storage, now) === 'completed';
}

/** A small warning when an explicit sign-in goes ahead over an unconfirmed sign-out: no ids, no tokens. */
export function warnLogoutStillPending(path: 'password' | 'sso' | 'sso-return' | 'sso-return-late'): void {
  console.warn(`auth: an earlier sign-out never reached the server; continuing with an explicit sign-in (${path})`);
}

export function clearLogoutPending(storage: UserScopedStorage | null = browserLocalStorage()): void {
  try {
    storage?.removeItem(LOGOUT_PENDING_KEY);
  } catch {
    // Storage refused: nothing to lift.
  }
}
